import { useState, useCallback, useMemo } from 'react';
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe, type StripeElementLocale } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Lock } from 'lucide-react';
import { Btn, Alert } from '@/presentation/component/ui';
import { formatCFA } from '@/presentation/util/format';
import type { PaymentIntent } from '@/domain/model/PaymentIntent';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface StripeFormInnerProps {
  paymentIntent: PaymentIntent;
  price: number;
  onSuccess: (clientSecret: string) => void;
  onError: (message: string) => void;
}

const StripeFormInner = ({ paymentIntent, price, onSuccess, onError }: StripeFormInnerProps) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cardElementOptions = useMemo(() => {
    const root = document.documentElement;
    const style = getComputedStyle(root);
    const inkColor = style.getPropertyValue('--color-ink').trim() || '#374151';
    const inkFaintColor = style.getPropertyValue('--color-ink-faint').trim() || '#9CA3AF';
    const badColor = style.getPropertyValue('--color-bad').trim() || '#EF4444';

    return {
      style: {
        base: {
          fontSize: '16px',
          color: inkColor,
          fontFamily: 'system-ui, sans-serif',
          '::placeholder': { color: inkFaintColor },
        },
        invalid: { color: badColor },
      },
      hidePostalCode: true,
    };
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (isSubmitting) return;

      setIsSubmitting(true);
      setError(null);

      const isDemoPayment =
        paymentIntent.clientSecret.includes('demo') ||
        !import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
        import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY.includes('pk_test_');

      if (isDemoPayment) {
        setTimeout(() => {
          setIsSubmitting(false);
          onSuccess(paymentIntent.clientSecret);
        }, 2000);
        return;
      }

      if (!stripe || !elements) {
        setError(t('payment.stripeNotLoaded'));
        setIsSubmitting(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError(t('payment.cardElementNotFound'));
        setIsSubmitting(false);
        return;
      }

      try {
        const { error: stripeError } = await stripe.confirmCardPayment(paymentIntent.clientSecret, {
          payment_method: { card: cardElement },
        });

        if (stripeError) {
          const msg = stripeError.message || t('payment.genericError');
          setError(msg);
          onError(msg);
        } else {
          onSuccess(paymentIntent.clientSecret);
        }
      } catch {
        const msg = t('payment.genericError');
        setError(msg);
        onError(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, paymentIntent.clientSecret, stripe, elements, onSuccess, onError, t],
  );

  const handleCardChange = useCallback((event: { error?: { message: string } }) => {
    setError(event.error?.message ?? null);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[var(--color-ink-soft)] mb-3">
          {t('payment.cardDetails')}
        </label>
        <div className="border border-[var(--color-line-strong)] rounded-lg p-3 focus-within:border-[var(--color-accent)] focus-within:ring-1 focus-within:ring-[var(--color-accent)]">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
      </div>

      {error && (
        <Alert kind="bad">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        </Alert>
      )}

      <div className="flex items-center gap-2 text-sm text-[var(--color-ink-faint)]">
        <Lock className="w-4 h-4" />
        <span>{t('payment.securePayment')}</span>
      </div>

      <Btn
        kind="primary"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
        isLoading={isSubmitting}
      >
        {isSubmitting ? t('payment.processing') : `${t('payment.payNow')} ${formatCFA(price)}`}
      </Btn>
    </form>
  );
};

interface StripePaymentFormProps {
  paymentIntent: PaymentIntent;
  price: number;
  onSuccess: (clientSecret: string) => void;
  onError: (message: string) => void;
}

export const StripePaymentForm = ({
  paymentIntent,
  price,
  onSuccess,
  onError,
}: StripePaymentFormProps) => {
  const { i18n } = useTranslation();
  const locale: StripeElementLocale = i18n.language.startsWith('en') ? 'en' : 'fr';

  const stripeOptions = useMemo(
    () => ({ clientSecret: paymentIntent.clientSecret, locale }),
    [paymentIntent.clientSecret, locale],
  );

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <StripeFormInner
        paymentIntent={paymentIntent}
        price={price}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};
